/*
 * Copyright (c) 2022 CalAmp Corp.  All Rights Reserved
 */
import {useCallback, useEffect, useMemo, useState} from 'react';

// defines the page meta data...
export interface PagingMeta {
  hasPrevious: boolean;
  hasNext: boolean;
  pageSize: number;
  total: number;
  count: number;
  pages: number;
  page: number;
};

// defines the usePagination hook props definition...
export type UsePaginationOptions = {
  initialPage?: number;
  pageSize?: number;
  reset?: boolean;
};

// defines the return definition of the usePagination hook...
export interface UsePaginationResult extends PagingMeta {
  nextPage: () => void;
  previousPage: () => void;
  gotoPage: (page: number) => void;
  setTotal: (count: number, forceUpdate?: boolean) => void;
  setPage: (count: number) => void;
  setPageSize: Function;
};

/**
 * The usePagination hook is responsible for managing the pagination state of the paginated pages.
 * The page holds the page size, total pages, page id, next and previous page availability and etc.
 * The above all properties are calculated based on the total result, page size, and page id.
 *
 * ```typescript
 *
 *  import {usePagination} from '@ion/query/src/hooks/';
 *
 *  const paging: UsePaginationResult = usePagination(initialPage, pageSize);
 *
 * ```
 */
export const usePagination = (initialPage = 1, initalPageSize = 20, reset = false): UsePaginationResult => {
  const [page, setPage]             = useState(initialPage);
  const [pageSize, setPageSize]     = useState(initalPageSize);
  const [totalCount, setTotalCount] = useState(0);

  const info = useMemo(() => {
    if (totalCount) {
      const pages       = Math.ceil(totalCount / (pageSize));
      const hasPrevious = page !== 1;
      const hasNext     = page < pages;
      const count       = page * pageSize;
      return {
        count : count > totalCount ? totalCount : count,
        total : totalCount,
        hasPrevious,
        hasNext,
        pageSize,
        pages,
        page
      };
    }
    return {
      hasPrevious : false,
      hasNext     : false,
      total       : 0,
      pages       : 1,
      page        : 1
    };
  }, [page, pageSize, totalCount]);

  const setTotal = useCallback((count: number, forceUpdate = false) => {
    if (count !== totalCount || forceUpdate) {
      setTotalCount(count);
    }
  }, [totalCount]);

  useEffect(() => {
    if (reset) {
      setPage(1);
    }
  }, [reset]);

  useEffect(() => {
    if (initalPageSize !== pageSize) {
      setPageSize(initalPageSize);
    }
  }, [initalPageSize]);

  const previousPage = useCallback(() => {
    if (info.hasPrevious) {
      setPage(page - 1);
    }
  }, [reset, info]);

  const nextPage = useCallback(() => {
    if (info.hasNext) {
      setPage(page + 1);
    }
  }, [reset, info]);

  const gotoPage = useCallback((newPage: number) => {
    if (newPage && newPage !== page && newPage <= info.pages) {
      setPage(newPage);
    }
  }, [reset, page, info]);

  return {
    previousPage,
    nextPage,
    gotoPage,
    setTotal,
    pageSize,
    setPageSize,
    setPage,
    ...info
  } as UsePaginationResult;
};

export default usePagination;
